import { useEffect, useLayoutEffect, useRef, useState } from "react";
import PreviewHeader from "../header";
import OrderTotals from "../../new-order/save-preview-delete/order-totals";
import ProcessingBreakdown from "../../supplier-pricing/processing-breakdown";
import { processingRows, ProcessingItem } from "../../supplier-pricing/processing-presentation";
import { fmt } from "../../supplier-pricing/ui";
import { useTranslation } from "react-i18next";
import { QuoteTotals } from "../../supplier-pricing/quote-document";

const PdfOrderSummary = ({ pageSize, order, rebars, nets, piles, handleRefs,
  totalPages, rightToLeft, pricingSnapshot }: any) => {
  const { t } = useTranslation("supplierPricing");
  const processingItems = processingRows(pricingSnapshot?.items).map(row => row.item);
  const detailChunks: ProcessingItem[][] = [];
  for (let index = 0; index < processingItems.length; index += 6) detailChunks.push(processingItems.slice(index, index + 6));
  const pageRef: any = useRef([]);
  const pricingRef = useRef<HTMLDivElement>(null);
  const [continued, setContinued] = useState(false);
  const [pricingReady, setPricingReady] = useState(!pricingSnapshot);
  useEffect(() => { pageRef.current = pageRef.current.slice(0, (continued ? 2 : 1) + detailChunks.length); handleRefs({ current: pageRef.current.filter(Boolean) }); }, [continued, detailChunks.length]);
  useLayoutEffect(() => {
    if (!pricingSnapshot) return;
    let active = true;
    let frame: number;
    const measure = () => {
      if (!active || !pricingRef.current) return;
      const page = pricingRef.current.closest(".pdf-document-page");
      if (!page || !page.getBoundingClientRect().height) { frame = requestAnimationFrame(measure); return; }
      if (!continued && pricingRef.current.getBoundingClientRect().bottom > page.getBoundingClientRect().bottom - 24) {
        setPricingReady(false); setContinued(true);
      } else setPricingReady(true);
    };
    measure();
    document.fonts?.ready.then(measure);
    return () => { active = false; cancelAnimationFrame(frame); };
  }, [pricingSnapshot, continued, rebars, nets, piles, pageSize]);
  const count = Object.values(totalPages || {}).reduce((sum: number, value: any) => sum + Number(value || 0), 0);
  const pages = continued ? [false, true] : [false];
  const pricing = <div className="pdf-order-pricing-summary" ref={pricingRef} data-pdf-pricing-summary data-pdf-resource-loading={pricingReady ? undefined : "true"}>
    <QuoteTotals snapshot={pricingSnapshot} presentation="order" showDetails={false} />
  </div>;
  return <>{pages.map((continuation, index) =>
    <div key={index} className="pdf-document-page pdf-order-summary-page" style={{ height: `${pageSize}px` }}
      ref={element => { pageRef.current[index] = element; }}>
      <PreviewHeader order={order} showDetails={false}
        pageNo={Math.max(1, Number(count) - (continued ? 1 : 0) - detailChunks.length + index)}
        totalPages={totalPages} rightToLeft={rightToLeft} />
      <div className="pdf-order-summary-page__content">
        {!continuation && <OrderTotals includeAsymmetricNets direction={rightToLeft ? "rtl" : "ltr"}
          rebars={rebars} nets={nets} piles={piles} />}
        {pricingSnapshot && (continued ? continuation : true) && pricing}
      </div>
    </div>
  )}{detailChunks.map((items, index) => {
    const processingTotal = Number(pricingSnapshot.totals.cut || 0) + Number(pricingSnapshot.totals.bend || 0) + Number(pricingSnapshot.totals.units || 0);
    return <div key={`processing-${index}`} className="pdf-document-page pdf-order-summary-page" style={{ height: `${pageSize}px` }}
      ref={element => { pageRef.current[pages.length + index] = element; }}>
      <PreviewHeader order={order} showDetails={false} pageNo={Math.max(1, Number(count) - detailChunks.length + index + 1)} totalPages={totalPages} rightToLeft={rightToLeft} />
      <div className="pdf-order-summary-page__content">
        <h3>{t("order.processingDetails")}</h3>
        <ProcessingBreakdown items={items} expectedTotal={processingRows(items).reduce((sum, row) => sum + (row.amount || 0), 0)} />
        {index === detailChunks.length - 1 && <p>{t("order.processingTotal")}: <bdi>{fmt(processingTotal)} ₪</bdi></p>}
      </div>
    </div>;
  })}</>;
};
export default PdfOrderSummary;
